import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { getAgent } from '@mkcore/agents/schema';
import { Badge, ErrorState, PageHeader, Skeleton } from '@mkcore/ui';
import { call } from '@/lib/api';
import { useErrorMessage } from '@/lib/mutation';
import { AgentChat, ChatWork } from '@/features/agents/chat';

export const Route = createFileRoute('/_app/agents/$id_/chat')({ component: ChatPage });

const statusTone = { active: 'success', paused: 'warning', terminated: 'neutral' } as const;

/** Your chat with an agent (CO-0857): the conversation, and beside it the work the agent holds. */
function ChatPage() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const message = useErrorMessage();
  const agent = useQuery({ queryKey: ['agent', 'agent', id], queryFn: () => call(getAgent, { id }) });
  if (agent.isError) return <ErrorState title={t('errors.generic')} description={message(agent.error)} />;
  if (!agent.data) return <Skeleton className="h-96" />;
  const a = agent.data;
  return (
    <>
      <Link to="/agents/$id" params={{ id }} className="mb-3 inline-flex items-center gap-1 text-sm text-fg-2 hover:text-fg-1">
        <ArrowLeft className="size-4" /> {a.name}
      </Link>
      <PageHeader
        eyebrow={a.role}
        title={t('agents.chat.title', { name: a.name })}
        description={
          <span className="inline-flex flex-wrap items-center gap-2 text-sm">
            <Badge tone={statusTone[a.status]}>{t(`agents.status.${a.status}`)}</Badge>
            {a.status !== 'active' && <span>{t('agents.chat.notActive')}</span>}
          </span>
        }
      />
      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        <AgentChat agentId={id} />
        <ChatWork agentId={id} />
      </div>
    </>
  );
}
